import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { getConnectorStatus } from './ConnectorService';
import { _isOneOf } from '../../utils/data.utils';
import { CONNECTOR_TYPES } from './connectors.constants';
import { Modal } from '../../components/modals/v2/helpers/v2.modal.helpers';
import { DeleteConnectorModalConfig } from '../../components/modals/v2/connectors/DeleteConnectorModal';
import { MSConnectorConfigContainerConfig } from './MSConnectorConfigContainer';
import ConnectorCard from './ConnectorCard';
import ConnectorActionsMenu from './ConnectorActionsMenu';
import {
  connectionStatusBadge,
  syncSubtitle,
  type Connection,
} from './connectors.view';

/** Types whose only management action is removing the connection. */
const DISCONNECT_ONLY = [
  'SECURITYSTUDIO',
  'QUALYSPC',
  'QUALYSCS',
  'QUALYSVM',
  'WIZER',
  'SECUREWORKS',
  'AWS',
  'SAFEGUARD',
  CONNECTOR_TYPES.GOOGLEWORKSPACE,
  CONNECTOR_TYPES.GSC,
  CONNECTOR_TYPES.CLOUDFLARE,
  CONNECTOR_TYPES.SOPHOS,
  CONNECTOR_TYPES.LANGFUSE,
  CONNECTOR_TYPES.GEMINI,
  CONNECTOR_TYPES.CLAUDE,
];

const STATUS_POLL_MS = 10000;

interface ConnectedTileProps {
  readonly dataArr: Connection[];
  readonly cbid: string | null;
  /** Present only for connectors that have a quick-view panel. */
  readonly onViewDetails?: (dataArr: Connection[]) => void;
  readonly tag?: string;
}

const ConnectedTile = ({
  dataArr,
  cbid,
  onViewDetails,
  tag,
}: ConnectedTileProps) => {
  const history = useHistory();
  const data = dataArr[0];
  const { name, connectorType, id: connectorId, lastPull } = data;
  // A polled status counts only for the connection it was polled for. The tile
  // stays mounted across a page refetch, so the connection it shows can change
  // underneath it, and a status kept from the old one would be wrong.
  const [polled, setPolled] = useState<{
    readonly connectorId: string;
    readonly status: string;
  } | null>(null);
  const status =
    polled?.connectorId === connectorId ? polled.status : data.connectorStatus;
  const ready = status === 'SUCCESS';

  useEffect(() => {
    if (ready) return undefined;
    const id = window.setInterval(() => {
      getConnectorStatus({ connectorId })
        .then((resp: any) =>
          setPolled({ connectorId, status: resp.data.status })
        )
        .catch(console.error.bind(console));
    }, STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [ready, connectorId]);

  const canViewDetails = !!onViewDetails && ready;
  const disconnectOnly = _isOneOf(connectorType, DISCONNECT_ONLY);

  const viewDetails = () => onViewDetails?.(dataArr);

  const openReport = () =>
    history.push(
      `/customer/connector/connector-details/${connectorType.toLowerCase()}`
    );

  const disconnect = () =>
    Modal.show(DeleteConnectorModalConfig, { data: { connectorId } });

  const configure = () =>
    Modal.show(MSConnectorConfigContainerConfig, {
      data: { data, cbid },
    });

  const menuItems = [
    ...(canViewDetails
      ? [
          { label: 'View details', onClick: viewDetails },
          { label: 'Open full report', onClick: openReport },
        ]
      : []),
    disconnectOnly
      ? { label: 'Disconnect', onClick: disconnect, danger: true }
      : { label: 'Configure', onClick: configure },
  ];

  return (
    <ConnectorCard
      name={name}
      connectorType={connectorType}
      subtitle={syncSubtitle(lastPull, ready)}
      logoUrl={data.logoUrl}
      tag={tag}
      status={connectionStatusBadge(status)}
      onSelect={canViewDetails ? viewDetails : undefined}
      // The card itself opens the quick view; the header keeps to status and
      // the menu so the name has room.
      actions={<ConnectorActionsMenu name={name} items={menuItems} />}
    />
  );
};

export default ConnectedTile;
